<template>
  <div class="number-sea-ai-container">   <!-- 新增唯一根容器 -->
    <!-- 悬浮按钮 - 位于左下角 -->
    <div class="ai-fab" @click="togglePanel" title="数海AI智能助手">
      <div v-if="!open" ref="lottieContainer" class="ai-fab-lottie"></div>
      <i v-else class="el-icon-close" style="font-size:28px;color:#764ba2"></i>
      <span v-if="unread > 0" class="ai-badge">{{ unread }}</span>
    </div>

    <!-- 聊天窗口 -->
    <transition name="ai-slide">
      <div v-if="open" class="ai-window" :class="{ maximized: isMaximized, dragging: isDragging, resizing: isResizing }" :style="windowStyle">
        <div class="ai-header" @mousedown="startDrag" @dblclick="toggleMaximize">
        <span>
          <i class="el-icon-magic-stick"></i>
          数海AI智能助手
        </span>
          <div class="ai-header-actions">
            <el-button link size="mini" @click="toggleMaximize" style="color:#9d1bd1;font-size:14px;opacity:0.9" :title="isMaximized ? '还原' : '最大化'">
              <i :class="isMaximized ? 'el-icon-news' : 'el-icon-full-screen'"></i>
            </el-button>
            <el-button link size="mini" @click="clearSession" style="color:#9d1bd1;font-size:12px;opacity:0.85">清空对话
            </el-button>
          </div>
        </div>

        <div class="ai-messages" ref="msgBox">
          <div v-if="loadingHistory" class="ai-loading-history">正在加载历史对话...</div>

          <div v-if="!loadingHistory && messages.length === 0" class="ai-welcome">
            <div class="welcome-icon">🤖</div>
            <p class="welcome-title">您好！我是数海AI智能助手 👋</p>
            <p class="welcome-desc">您可以问我企业数据查询相关的问题，我会为您精准匹配并提供数据下载服务</p>
            <div class="ai-quick-btns">
              <button v-for="q in quickQuestions" :key="q" @click="sendMsg(q)">{{ q }}</button>
            </div>
          </div>

          <div v-for="(msg, i) in messages" :key="i" :class="['ai-msg', msg.role]">
            <div class="ai-bubble" v-if="msg.type !== 'package_recommend'" v-html="formatMsg(msg.content)"></div>
            <!-- 下载按钮（仅在 type==='download' 时显示） -->
            <div v-if="msg.type === 'download'" class="ai-download-area">
              <el-button
                  type="primary"
                  size="mini"
                  :loading="msg.downloading"
                  icon="el-icon-download"
                  @click="downloadData(msg)">
                下载数据（{{ msg.total }} 条）
              </el-button>
              <span v-if="msg.downloadProgress" class="ai-download-hint">{{ msg.downloadProgress }}</span>
            </div>
            <!-- 数据包推荐卡片：文字气泡 + 卡片合在一条消息里 -->
            <div v-if="msg.type === 'package_recommend'" class="ai-package-wrapper">
              <div class="ai-bubble ai-bubble-package" v-html="formatMsg(msg.content)"></div>
              <div class="ai-package-card">
                <div class="ai-package-icon">📦</div>
                <div class="ai-package-info">
                  <div class="ai-package-name">{{ msg.packageName }}</div>
                  <div class="ai-package-reason">{{ msg.reason }}</div>
                  <div class="ai-package-cost">消耗 <span>{{ msg.costPoints }}</span> 点</div>
                </div>
                <el-button
                    type="primary"
                    size="mini"
                    :loading="msg.downloading"
                    icon="el-icon-download"
                    @click="downloadPackage(msg)">
                  立即下载
                </el-button>
                <span v-if="msg.downloadProgress" class="ai-download-hint">{{ msg.downloadProgress }}</span>
              </div>
            </div>
          </div>

          <div v-if="loading" class="ai-msg assistant">
            <div class="ai-bubble ai-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>

        <div class="ai-input">
          <el-input
              v-model="input"
              placeholder="请输入您的问题..."
              size="small"
              @keydown.enter.native.exact.prevent="sendMsg()"
              :disabled="loading"
          />
          <el-button type="primary" size="small" :loading="loading" @click="sendMsg()">发送</el-button>
        </div>

        <!-- 右下角缩放手柄（最大化时隐藏） -->
        <div v-if="!isMaximized" class="ai-resize-handle" @mousedown="startResize" title="拖动调整大小"></div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { BASE_URL } from '@/config/api'
import lottie from 'lottie-web'

// 用于二进制下载的专用 axios 实例：绕开全局拦截器，方便读取 headers + 原始 Blob
const downloadAxios = axios.create({
  baseURL: BASE_URL,
  timeout: 300000
})
// 自动带上 token（与全局 request.js 保持一致）
downloadAxios.interceptors.request.use(config => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    try {
      const user = JSON.parse(userStr)
      if (user && user.token) {
        config.headers['token'] = user.token
      }
    } catch (_) {}
  }
  return config
})

export default {
  name: 'NumberSeaAI',
  components: {},
  data() {
    return {
      aiRobotAnim: null,
      open: false,
      input: '',
      loading: false,
      loadingHistory: false,
      messages: [],
      unread: 0,
      msgBox: null,
      sessionId: '',
      historyLoaded: false,
      // ── 流式过滤代码块用的状态机 ──
      // inCodeBlock: 是否处于 markdown 代码块内（true = 隐藏）
      // tailBackticks: 上一 chunk 末尾的连续反引号数（0/1/2），用于处理 ``` 跨 chunk 的边界
      inCodeBlock: false,
      tailBackticks: 0,
      // ── 窗口位置 / 尺寸 / 拖动状态 ──（初始值在 mounted 里根据屏幕计算居中）
      winLeft: 0,
      winTop: 0,
      winWidth: 380,
      winHeight: 540,
      isMaximized: false,
      preMaxRect: null,    // 最大化前的位置/尺寸，用于还原
      isDragging: false,
      isResizing: false,
      dragStart: { x: 0, y: 0, left: 0, top: 0, width: 0, height: 0 },
      quickQuestions: [
        '找广州市制造业企业，在营的',
        '查北京市科技公司，注册资本500万以上',
        '找上海市在招商银行开户的批发零售业企业',
        '如何批量查询企业数据？'
      ]
    }
  },
  mounted() {
    // 窗口居中初始化
    this.winLeft = Math.max(0, Math.round((window.innerWidth - this.winWidth) / 2))
    this.winTop = Math.max(0, Math.round((window.innerHeight - this.winHeight) / 2))
    // 按当前用户隔离 sessionId，避免不同账号看到同一份历史
    this.sessionId = localStorage.getItem('ai_session_id_' + this.currentUserId) || ''
    this.msgBox = this.$refs.msgBox
    this.$nextTick(() => this.initLottie())
  },
  watch: {
    open(val) {
      if (val) {
        this.unread = 0
        this.$nextTick(() => {
          // 面板刚挂载，重新绑定 msgBox 引用
          this.msgBox = this.$refs.msgBox
          this.scrollBottom()
        })
        // 打开面板时拉取历史对话
        if (this.sessionId && !this.historyLoaded) {
          this.loadHistory()
        }
      } else {
        // 关闭面板后按钮重新显示，重新挂载 lottie
        this.$nextTick(() => this.initLottie())
      }
    },
    // 任意消息变化（push / content 追加 / 移除）都自动滚到底部
    messages: {
      handler() {
        this.$nextTick(() => this.scrollBottom())
      },
      deep: true
    }
  },
  methods: {
    initLottie() {
      if (!this.$refs.lottieContainer) return
      if (this._lottieAnim) {
        this._lottieAnim.destroy()
        this._lottieAnim = null
      }
      import('@/assets/imgs/AI Robot.json').then(animData => {
        if (!this.$refs.lottieContainer) return
        this._lottieAnim = lottie.loadAnimation({
          container: this.$refs.lottieContainer,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: animData.default || animData
        })
      })
    },
    togglePanel() {
      this.open = !this.open
    },
    /**
     * 拖动窗口（鼠标按住 header 区域移动）
     * 限制：不能拖出屏幕，最少留 100x60 的可见区域
     */
    startDrag(e) {
      // 按钮/链接上的点击不触发拖动
      if (e.target.closest('.ai-header-actions') || e.target.closest('button')) return
      if (this.isMaximized) return
      this.isDragging = true
      this.dragStart = {
        x: e.clientX, y: e.clientY,
        left: this.winLeft, top: this.winTop
      }
      // 防止文本被选中
      e.preventDefault()
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.endDrag)
    },
    onDrag(e) {
      if (!this.isDragging) return
      const dx = e.clientX - this.dragStart.x
      const dy = e.clientY - this.dragStart.y
      const newLeft = this.dragStart.left + dx
      const newTop = this.dragStart.top + dy
      const maxLeft = window.innerWidth - 100
      const maxTop = window.innerHeight - 60
      this.winLeft = Math.max(-(this.winWidth - 100), Math.min(newLeft, maxLeft))
      this.winTop = Math.max(0, Math.min(newTop, maxTop))
    },
    endDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.endDrag)
    },
    /**
     * 拖动右下角 handle 调整窗口大小
     */
    startResize(e) {
      if (this.isMaximized) return
      this.isResizing = true
      this.dragStart = {
        x: e.clientX, y: e.clientY,
        width: this.winWidth, height: this.winHeight
      }
      e.preventDefault()
      e.stopPropagation()
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.endResize)
    },
    onResize(e) {
      if (!this.isResizing) return
      const dx = e.clientX - this.dragStart.x
      const dy = e.clientY - this.dragStart.y
      this.winWidth = Math.max(280, Math.min(this.dragStart.width + dx, window.innerWidth - 20))
      this.winHeight = Math.max(220, Math.min(this.dragStart.height + dy, window.innerHeight - 20))
    },
    endResize() {
      this.isResizing = false
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.endResize)
    },
    /**
     * 切换最大化 / 还原
     */
    toggleMaximize() {
      if (this.isMaximized) {
        if (this.preMaxRect) {
          this.winLeft = this.preMaxRect.left
          this.winTop = this.preMaxRect.top
          this.winWidth = this.preMaxRect.width
          this.winHeight = this.preMaxRect.height
        }
        this.isMaximized = false
      } else {
        this.preMaxRect = {
          left: this.winLeft, top: this.winTop,
          width: this.winWidth, height: this.winHeight
        }
        this.winLeft = 0
        this.winTop = 0
        this.winWidth = window.innerWidth
        this.winHeight = window.innerHeight
        this.isMaximized = true
      }
      this.$nextTick(() => this.scrollBottom())
    },
    /**
     * 从后端拉取该 session 的历史对话
     * 只在面板第一次打开时拉一次
     */
    async loadHistory() {
      if (!this.sessionId) return
      this.loadingHistory = true
      try {
        // AI 后端是匿名访问（不需要 token），用纯净 axios 直接调
        const url = this.aiBaseUrl + '/api/chat/history/' + encodeURIComponent(this.sessionId)
        const res = await axios.get(url)
        const list = (res && res.data && res.data.history) || []
        if (Array.isArray(list) && list.length > 0) {
          this.messages = list.map(m => {
            // system_meta：下载按钮元数据，转为 download 类型消息
            if (m.role === 'system_meta' && m.type === 'download') {
              return {
                role: 'assistant',
                type: 'download',
                content: m.content || ('本次共找到 ' + m.total + ' 条企业数据'),
                plan: m.plan,
                total: m.total,
                history: true
              }
            }
            const msg = {
              role: m.role,
              content: m.content,
              history: true
            }
            // 恢复数据包推荐卡片
            if (m.type === 'package_recommend') {
              msg.type = 'package_recommend'
              msg.packageId = m.packageId
              msg.packageName = m.packageName
              msg.costPoints = m.costPoints
              msg.reason = m.reason
              msg.downloading = false
              msg.downloadProgress = ''
            }
            return msg
          })
          this.$nextTick(() => this.scrollBottom())
          this.$message.success(`已恢复 ${list.length} 条历史对话`)
        }
        this.historyLoaded = true
      } catch (e) {
        console.warn('拉取历史对话失败：', e)
      } finally {
        this.loadingHistory = false
      }
    },
    formatMsg(text) {
      if (!text) return ''
      // 1. 去掉完整的 ```...``` 代码块（含 ```json / ``` / ```xxx）
      // 2. 去掉"半截"代码块：从 ``` 开始但没闭合的（到字符串末尾）
      // 3. 兜底：去掉任何残留的孤立 ``` 字符（防 AI 偶尔输出 4 个反引号时残留）
      const cleaned = (text || '')
        .replace(/```(?:json|JSON)?[\s\S]*?```/g, '')
        .replace(/```[\s\S]*$/g, '')
        .replace(/```/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim()

      // 2. 转义HTML标签
      const escapeHtml = (str) => str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return escapeHtml(cleaned).replace(
        /(https?:\/\/[^\s&]+)/g,
        '<a href="$1" target="_blank" style="color:#409EFF">$1</a>'
      )
    },
    scrollBottom() {
      if (this.msgBox) {
        this.msgBox.scrollTop = this.msgBox.scrollHeight
      }
    },
    /**
     * 代码块状态机：流式过程中遇到 ``` 就切换显示/隐藏
     *  - 状态 this.inCodeBlock: 当前是否在 ```...``` 代码块内
     *  - 状态 this.tailBackticks: 上一 chunk 末尾留下的连续反引号（处理 ``` 跨 chunk 的边界）
     *
     * 输入：本次 delta 的原始字符串
     * 输出：过滤后（去掉代码块）应该展示的可见文本
     */
    filterCodeBlock(chunk) {
      if (!chunk) return ''
      // 把上一 chunk 末尾留下的反引号拼到本 chunk 开头
      const full = '`'.repeat(this.tailBackticks) + chunk

      let visible = ''
      let i = 0
      while (i < full.length) {
        // 三个连续反引号 → 切换状态
        if (i + 2 < full.length && full[i] === '`' && full[i + 1] === '`' && full[i + 2] === '`') {
          this.inCodeBlock = !this.inCodeBlock
          i += 3
        } else {
          // 普通字符：NORMAL 状态才保留
          if (!this.inCodeBlock) {
            visible += full[i]
          }
          i++
        }
      }

      // 计算新的 tailBackticks：扫描 full 末尾的连续反引号，最多 2 个
      // （因为 ``` 是 3 个，至少要留 2 个才能与下一 chunk 拼出 ```）
      let tail = 0
      for (let j = full.length - 1; j >= 0 && full[j] === '`' && tail < 2; j--) {
        tail++
      }
      this.tailBackticks = tail

      return visible
    },
    async sendMsg(text) {
      const msg = (text || this.input || '').trim()
      if (!msg || this.loading) return
      this.input = ''

      // 每次新会话重置代码块状态机
      this.inCodeBlock = false
      this.tailBackticks = 0

      this.messages.push({ role: 'user', content: msg })
      this.$nextTick(() => this.scrollBottom())
      this.loading = true

      try {
        // 调用AI后端的流式接口
        await this.streamChat({
          sessionId: this.sessionId,
          message: msg,
          onMeta: (data) => {
            if (data.session_id) {
              // 后端会原样返回前端发过去的 session_id（已可能带 userId_ 前缀），
              // 这里要做"幂等"处理：只有当 data.session_id 不带 userId_ 前缀时才拼接，
              // 否则保持原样。否则会出现 sessionId 每次都多一个 userId_ 的 bug，
              // 导致 Redis key 一直在变、历史永远查不到。
              const prefix = this.currentUserId + '_'
              this.sessionId = data.session_id.startsWith(prefix)
                ? data.session_id
                : prefix + data.session_id
              localStorage.setItem('ai_session_id_' + this.currentUserId, this.sessionId)
            }
          },
          onDelta: (data) => {
            // 1. 先用代码块状态机过滤掉本 chunk 内属于 ```...``` 的部分
            const visibleText = this.filterCodeBlock(data.content || '')

            // 2. 流式追加到当前 assistant 气泡
            const last = this.messages[this.messages.length - 1]
            if (visibleText) {
              if (last && last.role === 'assistant' && last.streaming) {
                last.content = (last.content || '') + visibleText
              } else {
                this.messages.push({ role: 'assistant', content: visibleText, streaming: true })
              }
            } else if (!last || last.role !== 'assistant' || !last.streaming) {
              // 即便本次 chunk 全在代码块里，也要先建一个占位气泡（避免 done 来时空指针）
              this.messages.push({ role: 'assistant', content: '', streaming: true })
            }
            this.$nextTick(() => this.scrollBottom())
          },
          onDone: (data) => {
            // 流结束，状态机重置
            this.inCodeBlock = false
            this.tailBackticks = 0

            const lastIndex = this.messages.length - 1
            const last = this.messages[lastIndex]

            if (last && last.role === 'assistant' && last.streaming) {
              if (data.type === 'package_recommend') {
                // Vue2 响应式限制：不能直接给已有对象加新属性
                // 必须用 splice 替换整个对象，触发视图更新
                this.$set(this.messages, lastIndex, {
                  role: 'assistant',
                  streaming: false,
                  content: data.reply || last.content,
                  type: 'package_recommend',
                  packageId: data.packageId,
                  packageName: data.packageName,
                  costPoints: data.costPoints,
                  reason: data.reason,
                  downloading: false,
                  downloadProgress: ''
                })
              } else {
                last.streaming = false
                last.content = data.reply || last.content
                if (data.type === 'result' && data.total > 0) {
                  this.messages.push({
                    role: 'assistant',
                    type: 'download',
                    content: '本次共找到 ' + data.total + ' 条企业数据',
                    plan: data.plan,
                    total: data.total
                  })
                } else if (data.type === 'result' && data.total === 0) {
                  this.messages.push({
                    role: 'assistant',
                    type: 'empty',
                    content: '未找到符合条件的企业数据，建议您减少查询条件（如只保留地区或行业）或者换个查询条件后再试。'
                  })
                }
              }
            } else {
              // 没有 streaming 气泡
              if (data.type === 'package_recommend') {
                this.messages.push({
                  role: 'assistant',
                  content: data.reply || '',
                  type: 'package_recommend',
                  packageId: data.packageId,
                  packageName: data.packageName,
                  costPoints: data.costPoints,
                  reason: data.reason,
                  downloading: false,
                  downloadProgress: ''
                })
              } else {
                this.messages.push({ role: 'assistant', content: data.reply || '' })
                if (data.type === 'result' && data.total > 0) {
                  this.messages.push({
                    role: 'assistant',
                    type: 'download',
                    content: '本次共找到 ' + data.total + ' 条企业数据',
                    plan: data.plan,
                    total: data.total
                  })
                } else if (data.type === 'result' && data.total === 0) {
                  this.messages.push({
                    role: 'assistant',
                    type: 'empty',
                    content: '未找到符合条件的企业数据，建议您减少查询条件（如只保留地区或行业）或者换个查询条件后再试。'
                  })
                }
              }
            }
            this.$nextTick(() => this.scrollBottom())
            if (!this.open) this.unread++
          },
          onError: (data) => {
            // 出错也要重置状态机
            this.inCodeBlock = false
            this.tailBackticks = 0
            this.messages.push({role: 'assistant', content: data.message || '抱歉，服务暂时不可用，请稍后重试'})
            this.$nextTick(() => this.scrollBottom())
          }
        })
      } catch (e) {
        this.messages.push({role: 'assistant', content: '抱歉，服务连接失败，请稍后重试'})
        this.$nextTick(() => this.scrollBottom())
      } finally {
        this.loading = false
      }
    },
    async clearSession() {
      try {
        this.$confirm('确认清空当前会话的历史对话吗？此操作不可恢复。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          customClass: 'ai-confirm-dialog',
          zIndex: 99999
        }).then(async () => {
          // 调用后端 DELETE 清理 Redis 中的历史
          if (this.sessionId) {
            try {
              await axios.delete(this.aiBaseUrl + '/api/chat/history/' + encodeURIComponent(this.sessionId))
            } catch (e) {
              console.warn('清理后端历史失败：', e)
            }
          }
          this.messages = []
          this.sessionId = ''
          this.historyLoaded = false
          localStorage.removeItem('ai_session_id_' + this.currentUserId)
          this.unread = 0
          this.$message.success('对话已清空')
        }).catch(() => {})
      } catch (e) {
        // 兜底：直接清空
        this.messages = []
        this.sessionId = ''
        this.historyLoaded = false
        localStorage.removeItem('ai_session_id_' + this.currentUserId)
        this.unread = 0
      }
    },
    /**
     * 调用AI后端的SSE流式接口（使用 XHR 累加 responseText）
     */
    streamChat({sessionId, message, onMeta, onDelta, onDone, onError}) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        let lastIndex = 0
        let buffer = ''
        xhr.open('POST', this.aiStreamUrl, true)
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.setRequestHeader('Accept', 'text/event-stream')
        xhr.setRequestHeader('Cache-Control', 'no-cache')

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 3 || xhr.readyState === 4) {
            // 取新增的部分
            const newText = xhr.responseText.substring(lastIndex)
            lastIndex = xhr.responseText.length
            buffer += newText
            // 解析 \n\n 分隔的SSE块
            let idx
            while ((idx = buffer.indexOf('\n\n')) !== -1) {
              const block = buffer.slice(0, idx)
              buffer = buffer.slice(idx + 2)
              this.handleSSEBlock(block, onMeta, onDelta, onDone, onError)
            }
          }
        }
        xhr.onerror = () => {
          reject(new Error('Network error'))
        }
        xhr.onload = () => {
          // 处理剩余buffer
          if (buffer.trim()) {
            this.handleSSEBlock(buffer, onMeta, onDelta, onDone, onError)
          }
          resolve()
        }

        xhr.send(JSON.stringify({sessionId, message}))
      })
    },
    handleSSEBlock(block, onMeta, onDelta, onDone, onError) {
      if (!block.trim()) return
      let event = 'message'
      let data = ''
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) {
          event = line.slice(6).trim()
        } else if (line.startsWith('data:')) {
          data += line.slice(5).trim()
        }
      }
      if (!data) return
      try {
        const parsed = JSON.parse(data)
        if (event === 'meta' && onMeta) onMeta(parsed)
        else if (event === 'delta' && onDelta) onDelta(parsed)
        else if (event === 'done' && onDone) onDone(parsed)
        else if (event === 'error' && onError) onError(parsed)
      } catch (e) {
        console.error('SSE parse error:', e)
      }
    },
    /**
     * 下载数据包 - 调用供需平台的 /data-package/download/{id} 接口（带权限验证与额度扣除）
     */
    async downloadPackage(msg) {
      if (!msg || !msg.packageId) return
      msg.downloading = true
      msg.downloadProgress = '正在下载数据包...'
      try {
        const res = await downloadAxios.get('/data-package/download/' + msg.packageId, { responseType: 'blob' })
        if (!res || !res.data) {
          throw new Error('返回数据为空')
        }

        // 判断是否是错误响应（后端用 JSON 返回错误）
        const contentType = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || ''
        const isJson = contentType.includes('application/json')
        const looksLikeError = res.status >= 400 || isJson || res.data.size < 512
        if (looksLikeError) {
          const txt = await res.data.text()
          let errMsg = txt
          try {
            const json = JSON.parse(txt)
            errMsg = json.msg || json.message || txt
          } catch (_) {}
          throw new Error(errMsg)
        }

        if (!(res.data instanceof Blob) || res.data.size === 0) {
          throw new Error('返回文件为空')
        }

        // 解析文件名
        const disp = (res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || ''
        const matchName = disp.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        const filename = matchName ? decodeURIComponent(matchName[1].replace(/['"]/g, ''))
          : (msg.packageName || '数据包') + '.zip'

        const url = window.URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        window.URL.revokeObjectURL(url)
        msg.downloadProgress = ''
        this.$message.success('数据包下载成功，已消耗 ' + msg.costPoints + ' 点')
      } catch (e) {
        console.error('数据包下载失败：', e)
        let errMsg = '下载失败，请重试'
        if (e && e.message) {
          errMsg = e.message
        }
        this.$message.error(errMsg)
        msg.downloadProgress = ''
      } finally {
        msg.downloading = false
      }
    },
    /**
     * 下载数据 - 调用供需平台的代理接口（带权限验证与额度扣除）
     */
    async downloadData(msg) {
      if (!msg || !msg.plan) return
      msg.downloading = true
      msg.downloadProgress = '正在提交下载任务...'
      try {
        // 用专用 axios（带 baseURL + token），responseType: 'blob' 让 data 直接是 Blob
        // 用 downloadAxios 而不是 request，是为了不被全局响应拦截器解包，
        // 这样能拿到原始 headers（含 Content-Disposition 中的中文文件名）
        const res = await downloadAxios.post('/ai/chat_download', { plan: msg.plan }, { responseType: 'blob' })

        if (!res || !res.data) {
          throw new Error('返回数据为空')
        }

        // 关键：先判断响应是不是错误，再决定是否触发下载
        // 后端 GlobalExceptionHandler 把 CustomException 转成 Result.error(msg)（HTTP 200 + JSON），
        // downloadAxios 又绕开了全局拦截器，responseType:'blob' 会把 JSON 包成 Blob。
        // 如果不检查，会把错误 JSON 当文件下载下来。
        const contentType = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || ''
        const isJson = contentType.includes('application/json')
        const looksLikeError = res.status >= 400 || isJson || res.data.size < 1024  // 错误响应通常 < 1KB
        if (looksLikeError) {
          const txt = await res.data.text()
          let errMsg = txt
          try {
            const json = JSON.parse(txt)
            errMsg = json.msg || json.message || txt
          } catch (_) {}
          throw new Error(errMsg)
        }

        if (!(res.data instanceof Blob) || res.data.size === 0) {
          throw new Error('返回文件为空')
        }

        // 解析文件名（从 Content-Disposition）
        const disp = (res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || ''
        const match = disp.match(/filename\*=UTF-8''([^;]+)/)
        const filename = match ? decodeURIComponent(match[1])
          : '企业查询结果_' + (msg.total || 0) + '条.zip'

        const url = window.URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        window.URL.revokeObjectURL(url)
        msg.downloadProgress = ''
        this.$message.success('下载完成')
      } catch (e) {
        console.error('下载失败：', e)
        // 尝试从错误里取后端返回的 msg
        let errMsg = '下载失败，请重试'
        if (e && e.response && e.response.data) {
          try {
            const txt = await e.response.data.text()
            const json = JSON.parse(txt)
            errMsg = json.msg || errMsg
          } catch (_) {}
        } else if (e && e.message) {
          errMsg = e.message
        }
        this.$message.error(errMsg)
        msg.downloadProgress = ''
      } finally {
        msg.downloading = false
      }
    },
  },
  computed: {
    /**
     * 窗口的内联样式（位置 + 尺寸）
     * 通过动态绑定让拖动 / 调整大小 / 最大化能实时生效
     */
    windowStyle() {
      return {
        left: this.winLeft + 'px',
        top: this.winTop + 'px',
        width: this.winWidth + 'px',
        height: this.winHeight + 'px'
      }
    },
    /**
     * 当前登录用户ID（用于 sessionId 隔离）
     * 从 localStorage.user.id 读取；未登录时为 'anonymous'
     */
    currentUserId() {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const user = JSON.parse(userStr)
          if (user && user.id != null) {
            return String(user.id)
          }
        }
      } catch (_) {}
      return 'anonymous'
    },
    // AI后端流式接口地址（直连AI后端，匿名可用）
    aiStreamUrl() {
      return 'https://ai.shuhailinghang.cn/api/chat/stream'
    },
    // AI后端基础地址
    aiBaseUrl() {
      return 'https://ai.shuhailinghang.cn'
    },
    // 供需平台代理下载地址（带权限验证 + 额度扣除）
    // 注意：这里只是兜底字符串，实际下载走 request（axios）会自动带 baseURL
    downloadUrl() {
      return '/api/ai/chat_download'
    }
  }
}
</script>

<style scoped>
/* 悬浮按钮 - 左下角 */
.ai-fab {
  position: fixed;
  left: 5px;
  bottom: 20px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9999;
  transition: transform 0.3s;
  font-size: 24px;
}

.ai-fab:hover {
  transform: scale(1.08);
}

.ai-fab-lottie {
  width: 200px;
  height: 200px;
  pointer-events: none;
}

.ai-fab:hover {
  transform: scale(1.08);
  box-shadow: none;
}

.ai-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #f56c6c;
  color: white;
  border-radius: 10px;
  font-size: 11px;
  padding: 1px 5px;
  min-width: 18px;
  text-align: center;
  font-weight: 600;
}

/* 聊天窗口 */
.ai-window {
  position: fixed;
  /* left/top/width/height 由 :style="windowStyle" 动态控制（支持拖动 / 调整大小 / 最大化） */
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 9998;
  overflow: hidden;
  border: 1px solid #ebeef5;
  /* 用 user-select:none + will-change 提升拖动手感 */
  user-select: none;
  -webkit-user-select: none;
  will-change: left, top, width, height;
}

/* 拖动 / 调整大小时禁用文本选择 */
.ai-window.dragging,
.ai-window.resizing {
  cursor: grabbing;
  user-select: none;
  -webkit-user-select: none;
}

/* 最大化态：覆盖整个视口 */
.ai-window.maximized {
  border-radius: 0;
  border: none;
  width: 100vw !important;
  height: 100vh !important;
  left: 0 !important;
  top: 0 !important;
}

.ai-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  /* 鼠标移到 header 上显示 move 光标，提示可拖动 */
  cursor: move;
  -webkit-app-region: drag;  /* 兼容部分 webkit 容器 */
}

.ai-header:hover {
  /* 微弱高亮提示可拖动 */
  background: linear-gradient(135deg, #7188ee 0%, #8256b2 100%);
}

.ai-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  /* header 内的按钮区不触发拖动 */
  cursor: default;
}

.ai-header i {
  margin-right: 6px;
  font-size: 16px;
}

/* 右下角缩放手柄 */
.ai-resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  /* 用线性渐变画一个 ↘ 三角 */
  background: linear-gradient(135deg,
    transparent 0%,
    transparent 40%,
    rgba(102, 126, 234, 0.35) 40%,
    rgba(102, 126, 234, 0.35) 55%,
    transparent 55%,
    transparent 70%,
    rgba(102, 126, 234, 0.35) 70%,
    rgba(102, 126, 234, 0.35) 85%,
    transparent 85%
  );
  /* 鼠标悬浮时加深 */
  transition: background 0.2s;
  z-index: 10;
}

.ai-resize-handle:hover {
  background: linear-gradient(135deg,
    transparent 0%,
    transparent 40%,
    rgba(102, 126, 234, 0.6) 40%,
    rgba(102, 126, 234, 0.6) 55%,
    transparent 55%,
    transparent 70%,
    rgba(102, 126, 234, 0.6) 70%,
    rgba(102, 126, 234, 0.6) 85%,
    transparent 85%
  );
}

.ai-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fafbfc;
}

.ai-messages::-webkit-scrollbar {
  width: 6px;
}

.ai-messages::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.ai-welcome {
  text-align: center;
  color: #666;
  font-size: 13px;
  padding: 20px 10px;
}
.ai-loading-history {
  text-align: center;
  color: #999;
  font-size: 12px;
  padding: 30px 10px;
}

.welcome-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.welcome-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 8px 0 !important;
}

.welcome-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin: 8px 0 16px 0 !important;
}

.ai-quick-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-top: 12px;
}

.ai-quick-btns button {
  background: linear-gradient(135deg, #f0f3ff 0%, #ede4ff 100%);
  border: 1px solid #c6d6ff;
  color: #667eea;
  border-radius: 14px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-quick-btns button:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transform: translateY(-2px);
}

.ai-msg {
  display: flex;
  animation: fadeIn 0.3s;
}

.ai-msg.user {
  justify-content: flex-end;
}

.ai-msg.assistant {
  justify-content: flex-start;
}

.ai-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.7;
  word-break: break-word;
  white-space: pre-wrap;
}

.ai-msg.user .ai-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 4px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.ai-msg.assistant .ai-bubble {
  background: white;
  color: #303133;
  border: 1px solid #ebeef5;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.ai-typing {
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 14px;
}

.ai-typing span {
  width: 7px;
  height: 7px;
  background: #999;
  border-radius: 50%;
  animation: bounce 1.2s infinite;
}

.ai-typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.ai-typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.6;
  }
  30% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 下载按钮区域 */
.ai-download-area {
  margin-top: 8px;
  max-width: 80%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

.ai-download-area ::v-deep .el-button {
  border-radius: 12px;
  font-size: 12px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #67c23a 0%, #18a058 100%);
  border-color: #18a058;
}

.ai-download-area ::v-deep .el-button:hover {
  background: linear-gradient(135deg, #5daf34 0%, #138f4d 100%);
  border-color: #138f4d;
  transform: translateY(-1px);
}

.ai-download-hint {
  font-size: 11px;
  color: #909399;
  margin-left: 4px;
}

/* 数据包推荐卡片 */
.ai-package-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 85%;
}

.ai-bubble-package {
  max-width: 100%;
}

.ai-package-card {
  margin-top: 8px;
  max-width: 85%;
  background: linear-gradient(135deg, #f5f0ff 0%, #ede4ff 100%);
  border: 1px solid #c6b0f0;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.ai-package-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.ai-package-info {
  flex: 1;
  min-width: 0;
}

.ai-package-name {
  font-size: 14px;
  font-weight: 600;
  color: #5b21b6;
  margin-bottom: 3px;
}

.ai-package-reason {
  font-size: 12px;
  color: #6d28d9;
  opacity: 0.8;
  line-height: 1.5;
  margin-bottom: 4px;
}

.ai-package-cost {
  font-size: 12px;
  color: #7c3aed;
}

.ai-package-cost span {
  font-weight: 700;
  color: #dc2626;
}

.ai-package-card ::v-deep .el-button {
  border-radius: 12px;
  font-size: 12px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  border-color: #764ba2;
  flex-shrink: 0;
}

.ai-package-card ::v-deep .el-button:hover {
  background: linear-gradient(135deg, #8256b2 0%, #7188ee 100%);
  transform: translateY(-1px);
}

.ai-input {
  padding: 12px 14px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  background: white;
}

.ai-slide-enter-active, .ai-slide-leave-active {
  transition: all 0.25s ease;
}

.ai-slide-enter-from, .ai-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* 响应式 */
@media (max-width: 768px) {
  .ai-window {
    /* 小屏幕下强制占满可视区（覆盖内联 width/height） */
    left: 0 !important;
    top: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    border-radius: 0;
    border: none;
  }
  /* 小屏幕下隐藏拖动和缩放手柄（已经全屏了） */
  .ai-resize-handle {
    display: none;
  }
  .ai-header {
    cursor: default;
  }

  .ai-fab {
    left: 12px;
    bottom: 12px;
  }
}
</style>

<style>
/* 清空对话确认弹窗 z-index 提升，确保在 AI 浮窗上方显示 */
.ai-confirm-dialog {
  z-index: 99999 !important;
}
.ai-confirm-dialog + .v-modal,
.el-overlay.ai-confirm-dialog {
  z-index: 99998 !important;
}
</style>
